// Central, approved business content for Mayden Films (Australian English, AUD).
// Contact details also come live from Site Settings; these are safe fallbacks.

export const BUSINESS = {
  name: 'Mayden Films',
  owner: 'Darwin',
  base: 'Cranbourne, Victoria',
  phone: '0449 995 495',
  phoneHref: 'tel:+61449995495',
  email: 'mf.maydenfilms@gmail.com',
  instagram: 'https://www.instagram.com/mayden_films',
  facebook: 'https://www.facebook.com/profile.php?id=100069937371000',
  googleReview: 'https://g.page/r/CYK6s7ubp5etECE/review',
  responseTime: 'Enquiries are normally answered within two business days.',
};

export const SERVICE_AREAS = [
  'Cranbourne',
  'City of Casey',
  'Pakenham',
  'Dandenong',
  'Frankston',
  'Mornington Peninsula',
  'Greater Melbourne (subject to availability and travel fees)',
];

export const SIGNATURE = {
  name: 'Mayden Films Signature Glam Booth',
  price: '$749',
  priceNote: 'AUD',
  duration: 'Up to three hours of booth operation',
  includes: [
    'Professional lighting',
    'Adjustable camera setup',
    'One standard fabric backdrop',
    'Unlimited digital photos during your booked operating time',
    'GIF options',
    'Short slow-motion video options',
    'Instant sharing via AirDrop, email or text',
    'Online event gallery',
    'Personalised photo overlay',
    'Personalised attract screen',
    'Digital props',
    'Setup and pack-down',
    'One Glam Booth attendant',
    'Travel within 30 kilometres of Cranbourne',
    'One design revision',
  ],
};

export const BOOKING_STEPS = [
  { step: 1, title: 'Submit your event details', description: 'Tell us the date, venue and celebration.' },
  { step: 2, title: 'Receive your personalised quote', description: 'We review availability and requirements.' },
  { step: 3, title: 'Sign & pay the 30% booking fee', description: 'This is what reserves your date.' },
  { step: 4, title: 'Complete the final questionnaire', description: 'Share the details that make it yours.' },
  { step: 5, title: 'Approve the personalised design', description: 'We craft your overlay and attract screen.' },
  { step: 6, title: 'Enjoy the Glam Booth', description: 'We handle setup, the session and pack-down.' },
  { step: 7, title: 'Receive your online gallery', description: 'All your images, ready to download and share.' },
];

// Optional upgrades — no numerical print caps, no artificial urgency.
export const UPGRADES = [
  {
    name: 'Printing Upgrade',
    price: '$150',
    headline: 'Unlimited Prints During Your Booked Glam Booth Time',
    points: [
      'One 4×6 print, or one matching pair of 2×6 photo strips, per completed booth session',
      'Guests may complete as many new booth sessions as the booked operating time allows',
      'Does not include unlimited duplicate reprints of the same session',
      'Available only during the booked Glam Booth operating time',
      'Choose one print format before the event',
      'Subject to normal equipment operation',
    ],
  },
  {
    name: 'Guestbook Package',
    price: '$120',
    headline: 'Available only with the Printing Upgrade',
    points: [
      'One black or white event guestbook (subject to availability)',
      'One additional guestbook print from each completed session',
      'Adhesive supplies, plus metallic and black writing pens',
      'Small guestbook instruction sign',
      'Attendant encouragement for guests to leave messages',
      'Completed guestbook handed to your nominated person at the end of the event',
    ],
  },
];

export const EXTRA_UPGRADES = [
  { name: 'Additional operating hour', price: '$150' },
  { name: 'Premium backdrop', price: 'from $100 + any external hire/purchase/delivery costs' },
  { name: 'Red carpet and bollards', price: '$100' },
  { name: 'Custom animated screen or advanced design', price: 'from $75' },
  { name: 'Rush template preparation', price: '$50' },
  { name: 'Sunday or public-holiday booking surcharge', price: '$100' },
  { name: 'Travel outside the included area', price: 'based on the approved travel zones' },
];

export const PACKAGE_EXAMPLES = [
  { name: 'Signature Glam Booth', price: '$749' },
  { name: 'Signature Glam Booth with Printing', price: '$899' },
  { name: 'Signature Glam Booth with Printing and Guestbook', price: '$1,019' },
];

export const BACKDROPS = [
  { name: 'Classic Light', description: 'White or cream fabric', included: true },
  { name: 'Classic Dark', description: 'Black fabric', included: true },
];

export const TRAVEL_ZONES = [
  { range: 'Up to 30 km', fee: 'Included' },
  { range: '31–50 km', fee: '$75' },
  { range: '51–75 km', fee: '$150' },
  { range: '76–100 km', fee: '$250' },
  { range: 'More than 100 km', fee: 'Custom quote (or booking may be declined)' },
];

export const WHY_CHOOSE = [
  { title: 'Personal service from Darwin', description: 'Every booking is prepared and run personally.', icon: 'Heart' },
  { title: 'Professional equipment & lighting', description: 'Studio-grade gear for a polished result.', icon: 'Lightbulb' },
  { title: 'Personalised event design', description: 'Custom overlay and attract screen for your event.', icon: 'Palette' },
  { title: 'Clear package & booking process', description: 'Transparent pricing with no guesswork.', icon: 'ClipboardCheck' },
  { title: 'Digital sharing & online gallery', description: 'Guests share instantly; you keep the gallery.', icon: 'Share2' },
  { title: 'Optional unlimited prints', description: 'Add printing for a keepsake on the night.', icon: 'Printer' },
  { title: 'Bookings confirmed individually', description: 'No fixed monthly cap — every event gets careful, personal attention.', icon: 'CalendarCheck' },
];

// Public portfolio filter categories (shared with the admin event editor).
export const PORTFOLIO_CATEGORIES = [
  { value: 'birthdays-debuts', label: 'Birthdays & Debuts' },
  { value: 'maternity-family', label: 'Maternity & Family' },
  { value: 'christenings', label: 'Christenings' },
  { value: 'weddings-engagements', label: 'Weddings & Engagements' },
  { value: 'corporate-community', label: 'Corporate & Community' },
  { value: 'glambooth', label: 'Glam Booth' },
];

// Home page 4-service overview.
export const SERVICE_OVERVIEW = [
  { name: 'Glam Booth', price: 'from $399', icon: 'Camera', to: '/glam-booth',
    description: 'Four premium packages with professional lighting, personalised design and instant digital sharing.' },
  { name: 'Event Photography', price: 'from $249', icon: 'Aperture', to: '/photography',
    description: 'Weddings, celebrations and portrait sessions with clear packages and edited galleries delivered online.' },
  { name: 'Highlight Videography', price: 'from $599', icon: 'Film', to: '/videography',
    description: 'A cinematic highlight film and social teaser that capture the feeling of your day.' },
  { name: 'Mayden Messages', price: 'add $150', icon: 'Sparkles', to: '/glam-booth',
    description: 'A cinematic video guestbook of your guests\u2019 heartfelt words \u2014 included in the Ultimate package.' },
];

export const EVENT_TYPES = [
  'Wedding', 'Engagement', 'Birthday or Debut', 'Anniversary',
  'Christening or Baptism', 'Maternity', 'Newborn', 'Family',
  'Portrait or Creative Shoot', 'Graduation or Formal', 'Corporate or Community',
  'Commercial, Product or Food', 'Automotive', 'Sports or Fitness',
  'Concert or Live Performance', 'Other Event or Creative Project',
];

// Event types where an "Estimated guest count" makes sense (vs. a shoot).
export const GUEST_COUNT_TYPES = [
  'Wedding', 'Engagement', 'Birthday or Debut', 'Anniversary',
  'Christening or Baptism', 'Corporate or Community',
  'Concert or Live Performance', 'Other Event or Creative Project',
];
